import shortid from 'shortid';


/**
 * Clipboard functionality.
 * @example
 * <template v-if="Clipboard.isSupported">
 *     <button
 *      @click="() => Clipboard.copy('text')
 *          .then(() => { // show $toast "copied" })
 *          .catch(() => { // show modal copy manually })">
 *          Click to copy
 *      </button>
 * </template>
 * <template v-else>
 *     <div>Please copy manually</div>
 * </template>
 * <template v-if="showCopyModal">
 *     <p>Please copy manually</p>
 *     <p>value</p>
 * </template>
 * https://github.com/zenorocha/clipboard.js
 */
export default class Clipboard {
    static Command = 'copy';

    constructor({text}) {
        this.id = `clipboard-${shortid.generate()}`;
        this.text = text;
    }

    exec() {
        this.element = this.makeFakeElement();
        document.body.appendChild(this.element);
        this.element.innerHTML = this.text;
        this.element.select();
        document.execCommand(Clipboard.Command);
        this.element.blur();
    }

    destroy() {
        this.removeFakeElement();
    }

    makeFakeElement() {
        const isRTL = document.documentElement.getAttribute('dir') === 'rtl';
        const node = document.createElement('textarea');
        node.id = this.id;
        // Prevent zooming on iOS
        node.style.fontSize = '12pt';
        // Reset box model
        node.style.border = '0';
        node.style.padding = '0';
        node.style.margin = '0';
        // Move element out of screen horizontally
        node.style.position = 'absolute';
        node.style[isRTL ? 'right' : 'left'] = '-9999px';
        node.setAttribute('readonly', '');
        return node;
    }

    removeFakeElement() {
        document.body.removeChild(this.element);
    }

    static copy(text) {
        return new Promise((resolve, reject) => {
            const instance = new Clipboard({text});
            try {
                instance.exec();
                resolve();
            } catch (error) {
                reject(error);
            } finally {
                instance.destroy();
            }
        });
    }

    static get isSupported() {
        return !!document.queryCommandSupported(Clipboard.Command);
    }
}
